import { format } from 'date-fns'
import QRCode from 'qrcode'
import { toast } from 'sonner'
import { useCallback, useEffect, useState } from 'react'

import { DeviceLinkCode, SSO_QUERY_PARAM } from '@iws/backoffice-api'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { AuthModel } from '@/http/models/auth'
import { BranchModel, BranchOption } from '@/http/models/Branch'
import { DeviceModel, LinkedDevice } from '@/http/models/Device'

interface PosDevicesProps {
    businessProfile: { platform_pos_domain?: string; custom_pos_domain?: string } | null
}

const errorOf = (error: unknown, fallback: string) =>
    (error as { data?: { message?: string } } | null)?.data?.message ?? fallback

const PLATFORM_LABEL: Record<LinkedDevice['platform'], string> = { android: 'Android app', ios: 'iOS app', web: 'Web POS' }

/**
 * Connected POS devices (POS.md §12.9.2 "App linking", O2). *Connect* asks which branch the device works in (skipped
 * with one branch, §18.2), then shows a code as a QR with the text below; the web till or the mobile app types or
 * scans it. Unlinking keeps the device's data; it must connect again.
 */
export const PosDevices = ({ businessProfile }: PosDevicesProps) => {
    const [devices, setDevices] = useState<LinkedDevice[]>([])
    const [loading, setLoading] = useState(true)
    const [linkCode, setLinkCode] = useState<(DeviceLinkCode & { qr: string }) | null>(null)
    const [busy, setBusy] = useState(false)
    // The branch step of Connect: open while choosing, with the business's branches.
    const [branches, setBranches] = useState<BranchOption[] | null>(null)
    const [branchId, setBranchId] = useState('')

    const posDomain = businessProfile?.custom_pos_domain || businessProfile?.platform_pos_domain

    const load = useCallback(async () => {
        setLoading(true)
        const res = await DeviceModel.getList()
        if (res.success && res.data) setDevices(res.data.result)
        else toast.error(errorOf(res.error, 'Could not load devices'))
        setLoading(false)
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const createCode = async (branch: string) => {
        setBusy(true)
        const res = await DeviceModel.createLinkCode({ branch_id: branch })
        setBusy(false)
        if (!res.success || !res.data) {
            toast.error(errorOf(res.error, 'Could not create a code'))
            return
        }
        const qr = await QRCode.toDataURL(res.data.code, { width: 320, margin: 1 })
        setBranches(null)
        setLinkCode({ ...res.data, qr })
    }

    const connect = async () => {
        setBusy(true)
        const res = await BranchModel.getOptions()
        setBusy(false)
        const list = res.success && res.data ? res.data.result : []
        if (list.length === 0) {
            toast.error(errorOf(res.error, 'Add a branch first: a device works in one branch.'))
            return
        }
        if (list.length === 1) return createCode(list[0]._id)
        setBranchId(list[0]._id)
        setBranches(list)
    }

    const closeCode = () => {
        setLinkCode(null)
        // The device may have connected while the code was shown.
        load()
    }

    // Opens the web till signed in as the same staff member (SSO hand-off, POS.md §12.9.2); it still needs connecting.
    const openPos = async () => {
        const tab = window.open('', '_blank')
        const res = await AuthModel.createSsoTicket()
        if (res.success && res.data && posDomain) {
            if (tab) tab.location.href = `https://${posDomain}/?${SSO_QUERY_PARAM}=${res.data.ticket}`
        } else {
            tab?.close()
            toast.error(errorOf(res.error, 'Could not open the POS'))
        }
    }

    const unlink = async (device: LinkedDevice) => {
        const res = await DeviceModel.unlink(device._id)
        if (res.success) {
            toast.success('Device unlinked', { description: 'It keeps its sales and must connect again.' })
            load()
        } else toast.error(errorOf(res.error, 'Could not unlink the device'))
    }

    return (
        <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
                <div>
                    <CardTitle>Connected devices</CardTitle>
                    <CardDescription>Web POS and POS apps that sell and sync for this store.</CardDescription>
                </div>
                <div className="flex gap-2">
                    {posDomain && (
                        <Button variant="outline" onClick={openPos}>
                            Open POS
                        </Button>
                    )}
                    <Button onClick={connect} disabled={busy}>
                        {busy && <Spinner />}
                        Connect
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="flex justify-center py-8">
                        <Spinner />
                    </div>
                ) : devices.length === 0 ? (
                    <p className="py-8 text-center text-sm text-muted-foreground">No devices connected yet.</p>
                ) : (
                    <ul className="divide-y divide-border-subtle">
                        {devices.map((d) => (
                            <li key={d._id} className="flex items-center justify-between gap-4 py-3">
                                <div className="min-w-0">
                                    <p className="truncate text-sm text-ink">{d.name || PLATFORM_LABEL[d.platform]}</p>
                                    <p className="truncate text-xs text-muted-foreground">
                                        {PLATFORM_LABEL[d.platform]}
                                        {d.branch?.name ? ` · ${d.branch.name}` : ''} · connected {format(new Date(d.linked_at), 'd MMM yyyy')}
                                        {d.last_seen_at ? ` · last sync ${format(new Date(d.last_seen_at), 'd MMM, HH:mm')}` : ''}
                                    </p>
                                </div>
                                <Button variant="outline" size="sm" onClick={() => unlink(d)}>
                                    Unlink
                                </Button>
                            </li>
                        ))}
                    </ul>
                )}
            </CardContent>

            <Dialog open={!!branches} onOpenChange={(open) => !open && setBranches(null)}>
                <DialogContent size="sm">
                    <DialogHeader>
                        <DialogTitle>Connect a device</DialogTitle>
                        <DialogDescription>
                            A device sells and syncs for one branch. To move it later, unlink it and connect it again.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-1.5 py-2">
                        <Label htmlFor="connect-branch">Branch</Label>
                        <Select value={branchId} onValueChange={setBranchId}>
                            <SelectTrigger id="connect-branch">
                                <SelectValue placeholder="Choose a branch" />
                            </SelectTrigger>
                            <SelectContent>
                                {branches?.map((b) => (
                                    <SelectItem key={b._id} value={b._id}>
                                        {b.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setBranches(null)} disabled={busy}>
                            Cancel
                        </Button>
                        <Button onClick={() => createCode(branchId)} disabled={busy || !branchId}>
                            {busy && <Spinner />}
                            Show code
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!linkCode} onOpenChange={(open) => !open && closeCode()}>
                <DialogContent size="sm">
                    <DialogHeader>
                        <DialogTitle>Connect a device</DialogTitle>
                        <DialogDescription>
                            On the web POS or the POS app, scan this QR code or type the code. The device will work in{' '}
                            {linkCode?.branch.name}. The code works once, until{' '}
                            {linkCode ? format(new Date(linkCode.expires_at), 'HH:mm') : ''}.
                        </DialogDescription>
                    </DialogHeader>
                    {linkCode && (
                        <div className="flex flex-col items-center gap-3 py-2">
                            <img src={linkCode.qr} alt="QR code to connect a device" className="size-56 rounded-md border border-border" />
                            <p className="text-center text-display tabular-nums tracking-[0.4em] text-ink">{linkCode.code}</p>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </Card>
    )
}
